import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useScheduleBoardModals } from 'af-root/hooks/useScheduleBoardModal';
import Loading from 'af-root/scenes/Company/ScheduleBoard/Shared/LoadingModal';
import ResourceSkillList from 'af-root/scenes/Company/ScheduleBoard/Shared/ResourceModals/SkillsList';

import type ScheduleBoardViewModel from 'ab-viewModels/scheduleBoard.viewModel';
import type ScheduleBoardEquipmentModalVM from 'ab-viewModels/scheduleBoardEquipmentModal.viewModel';
import type { ScheduleBoardOnDateViewModel } from 'ab-viewModels/scheduleBoardWorkOrdersOnDateView.viewModel';

import type ScheduleBoardWorkOrderVM from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';
import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatDate } from '@acceligentllc/shared/utils/time';

import { AssignableResourceType } from '@acceligentllc/shared/enums/assignableResourceType';
import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { AdditionalColors } from '@acceligentllc/shared/enums/color';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { MAX_EQUIPMENT_ASSIGNMENTS_PER_DAY } from 'ab-common/constants/scheduleBoard';
import { DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import ImageTag from 'af-components/Image';
import LabelWithColor from 'af-components/LabelWithColor';
import LockedValue from 'af-components/LockedValue';

import SettingsButton from './SettingsButton';
import styles from './styles.module.scss';
import AssignmentsModalSection from '../../AssignmentsModalSection';

interface OwnProps {
	createEquipmentAssignment: (
		type: AssignableResourceType,
		date: string,
		workOrderId: number,
		isAvailable: boolean,
		resource: ScheduleBoardEquipmentModalVM
	) => boolean;
	hasPermissionsToEditScheduleBoard: boolean;
	removeEquipmentAssignment: (dueDate: string, workOrderId: number, workOrderCode: string, equipmentId: number) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const _getStateToProps = (
	equipment: Nullable<ScheduleBoardEquipmentModalVM>,
	date: Nullable<string>,
	workOrdersByDateDictionary: ScheduleBoardViewModel['workOrdersByDateDictionary']
): {
	assignedWorkOrders: ScheduleBoardWorkOrderViewModel[];
	assignableWorkOrders: ScheduleBoardWorkOrderVM[];
	returnDate: string | undefined;
	loaned: boolean;
} => {
	if (!equipment || !date || !workOrdersByDateDictionary || !workOrdersByDateDictionary[date]) {
		return {
			assignedWorkOrders: [],
			assignableWorkOrders: [],
			returnDate: undefined,
			loaned: false,
		};
	}
	const workOrdersDictionary = workOrdersByDateDictionary[date];
	const assignedWorkOrderCodes = workOrdersDictionary?.equipmentAssignments?.[equipment?.id] ?? [];
	const assignedWorkOrders = assignedWorkOrderCodes
		.map((workOrderCode) => workOrdersDictionary?.workOrders?.[workOrderCode])
		.filter((_wo): _wo is ScheduleBoardOnDateViewModel['workOrders'][string] => (!!_wo));

	const returnDate = workOrdersDictionary.equipmentReturnDate[equipment?.id];
	const loanedEquipment = Object.values(workOrdersDictionary.toolbarEquipment.loaned);

	return {
		assignedWorkOrders,
		assignableWorkOrders: Object.keys(workOrdersDictionary?.workOrders ?? {})
			.reduce((_acc, _woCode) => {
				if (
					!assignedWorkOrderCodes.includes(_woCode)
					&& workOrdersDictionary?.workOrders?.[_woCode]?.status !== WorkOrderStatus.CANCELED
					&& workOrdersDictionary?.workOrders?.[_woCode]?.status !== WorkOrderStatus.LOCKED
					&& !workOrdersDictionary?.workOrders?.[_woCode]?.isPaused
					&& !workOrdersDictionary?.workOrders[_woCode]?.locked
				) {
					_acc.push(workOrdersDictionary!.workOrders[_woCode]);
				}
				return _acc;
			}, [] as ScheduleBoardWorkOrderVM[]),
		returnDate,
		loaned: loanedEquipment.flat().includes(equipment.id),
	};
};
const EquipmentModal: React.FC<Props> = (props) => {
	const {
		createEquipmentAssignment,
		removeEquipmentAssignment,
		hasPermissionsToEditScheduleBoard,
		workOrdersByDateDictionary,
		isAllowedToManagePerDiems,
	} = props;

	const { equipment, equipmentModalDate, showEquipmentModal, setEquipmentModalVisibility, setEquipmentWOHistoryModalVisibility } = useScheduleBoardModals();

	const {
		returnDate,
		assignableWorkOrders,
		assignedWorkOrders,
		loaned,
	} = React.useMemo(() => {
		return _getStateToProps(equipment, equipmentModalDate, workOrdersByDateDictionary);
	}, [equipment, equipmentModalDate, workOrdersByDateDictionary]);

	const closeModal = React.useCallback(() => {
		setEquipmentModalVisibility(false);
	}, [setEquipmentModalVisibility]);

	const createAssignment = React.useCallback(async (workOrderId: number) => {
		if (!hasPermissionsToEditScheduleBoard) {
			return false;
		}

		if (!equipment || !equipmentModalDate) {
			throw new Error('Equipment assignment not properly defined');
		}

		return await createEquipmentAssignment(
			AssignableResourceType.EQUIPMENT,
			equipmentModalDate,
			workOrderId,
			equipment.equipmentStatus?.available ?? true,
			equipment
		);
	}, [createEquipmentAssignment, equipmentModalDate, equipment, hasPermissionsToEditScheduleBoard]);

	const removeAssignment = React.useCallback((workOrderId: number, workOrderCode: string, itemId: number) => {
		if (!hasPermissionsToEditScheduleBoard) {
			return;
		}

		if (!equipmentModalDate) {
			throw new Error('Equipment assignment not properly defined');
		}

		removeEquipmentAssignment(equipmentModalDate, workOrderId, workOrderCode, itemId);
	}, [equipmentModalDate, hasPermissionsToEditScheduleBoard, removeEquipmentAssignment]);

	const openEquipmentWorkOrderHistoryModal = React.useCallback(() => {
		setEquipmentModalVisibility(false);
		setEquipmentWOHistoryModalVisibility(true);
	}, [setEquipmentModalVisibility, setEquipmentWOHistoryModalVisibility]);

	const renderDataModalContent = React.useCallback(() => {
		if (!equipment) {
			throw new Error('Equipment assignment not properly defined');
		}
		const {
			id,
			name,
			specification,
			equipmentCategory,
			equipmentStatus,
			frontImageUrl,
			backImageUrl,
			licenses,
			jobCode,
			skills,
			inspectionStickerExpiration,
			vinNumber,
			gvw,
			year,
			make,
			model,
			driversAlertId,
			tollPassId,
			hutNumber,
			plateNumber,
			plateType,
			registrationState,
			registrationExpiration,
		} = equipment;

		const equipmentStatusName = equipmentStatus?.name ?? 'Available';

		const assignedInOffices = assignedWorkOrders.filter((wo) => !!wo.officeNickname);

		const scheduleBoardBaseClassName = 'schedule-board-modal';
		const headerClassName = `${scheduleBoardBaseClassName}__big-header`;
		const headerLeft = `${scheduleBoardBaseClassName}__big-header-left`;
		const headerRight = `${scheduleBoardBaseClassName}__big-header-right`;

		return (
			<>
				<CustomModal.Header className={`${styles[headerClassName]}`}>
					<div className={`${styles[headerLeft]} `}>
						<span className="schedule-board-modal__resource__header__title">
							{name}
							{specification
								? <span className="schedule-board-modal__resource__header__subtitle"> {specification}</span>
								: null
							}
						</span>
					</div>
					<div className={`${styles[headerRight]} `}>
						<LabelWithColor color={equipment.color ?? AdditionalColors.GREY} text={equipmentCategory} />
						<SettingsButton
							openEquipmentWorkOrderHistoryModal={openEquipmentWorkOrderHistoryModal}
						/>
					</div>
				</CustomModal.Header >
				<CustomModal.Body>
					<div className="schedule-board-modal__resource__row schedule-board-modal__resource__row--even">
						<div className="schedule-board-modal__resource__avatar-wrapper schedule-board-modal__resource__avatar-wrapper--non-marginated">
							<ImageTag
								className="schedule-board-modal__resource__avatar"
								fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
								minSize={BlobStorageImageSizeContainer.SIZE_200X200}
								src={frontImageUrl}
								tryOriginal={true}
								tryRoot={true}
							/>
						</div>
						<div className="schedule-board-modal__resource__avatar-wrapper schedule-board-modal__resource__avatar-wrapper--non-marginated">
							<ImageTag
								className="schedule-board-modal__resource__avatar"
								fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
								minSize={BlobStorageImageSizeContainer.SIZE_200X200}
								src={backImageUrl}
								tryOriginal={true}
								tryRoot={true}
							/>
						</div>
					</div>
					<hr />
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m m-t-m">
							<SegmentLabel
								label="Requirements"
								tooltip="Licenses required to operate this equipment."
							/>
						</div>
						<Row className="row--non-padded">
							<Col md={6}>
								<LockedValue
									label="Needed Licenses"
									value={licenses}
								/>
							</Col>
							<Col md={18}>
								<ResourceSkillList skills={skills} title="Needed skills" />
							</Col>
						</Row>
					</div>
					<hr />
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m m-t-m">
							<SegmentLabel
								label="Current Status"
								tooltip="The current status of this equipment, including its status, expected return date, and the job and location it's currently assigned to."
							/>
						</div>
						<Row className="row--non-padded">
							<Col md={6}>
								<LockedValue
									label="Status"
									value={equipmentStatusName}
								/>
							</Col>
							<Col md={6}>
								<LockedValue
									label="Return date"
									value={returnDate}
								/>
							</Col>
							<Col md={6}>
								<LockedValue
									label="Job"
									value={jobCode}
								/>
							</Col>
							<Col md={6}>
								<LockedValue
									label="Job Locations"
									value={assignedInOffices.length > 0
										? assignedInOffices.map((wo) => wo.officeNickname).join(', ')
										: '-'
									}
								/>
							</Col>
							<Col md={12} />
						</Row>
					</div>
					<hr />
					<div className="schedule-board-modal__resource__row">
						<div className="m-b-m m-t-m">
							<SegmentLabel
								label="Equipment Information"
								tooltip="Vehicle and registration details for this equipment, including plate, registration, inspection, and identification information."
							/>
						</div>
						<Row className="row--padded">
							<Col md={6}>
								<LockedValue label="Plate Number" value={plateNumber ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="Plate Type" value={plateType ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="Registration State" value={registrationState ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue
									label="Registration Exp."
									value={registrationExpiration
										? formatDate(registrationExpiration, TimeFormat.DATE_ONLY_SLASHES)
										: '-'}
								/>
							</Col>
						</ Row>

						<Row className="row--padded">
							<Col md={6}>
								<LockedValue label="HUT Number" value={hutNumber ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue
									label="Inspection Exp."
									value={inspectionStickerExpiration
										? formatDate(inspectionStickerExpiration, TimeFormat.DATE_ONLY_SLASHES)
										: '-'}
								/>
							</Col>
							<Col md={6}>
								<LockedValue label="VIN" value={vinNumber ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="GVW" value={gvw !== null ? gvw.toString() : '-'} />
							</Col>
						</Row>
						<Row className="row--padded">
							<Col md={6}>
								<LockedValue label="Year" value={year ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="Make" value={make ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="Model" value={model ?? '-'} />
							</Col>
							<Col md={6}>
								<LockedValue label="Drivers Alert ID" value={driversAlertId ?? '-'} />
							</Col>
						</Row>
						<Row className="row--padded">
							<Col md={6}>
								<LockedValue label="Toll Pass ID" value={tollPassId ?? '-'} />
							</Col>
						</ Row>

					</div>
					<hr />
					<AssignmentsModalSection
						assignableWorkOrders={assignableWorkOrders}
						assignedWorkOrders={assignedWorkOrders}
						createAssignment={createAssignment}
						isAllowedToManagePerDiems={isAllowedToManagePerDiems}
						isDeletable={!equipment.isDeleted && hasPermissionsToEditScheduleBoard}
						isEditable={!equipment.isDeleted && hasPermissionsToEditScheduleBoard && !loaned}
						itemId={id}
						maxItems={MAX_EQUIPMENT_ASSIGNMENTS_PER_DAY}
						removeAssignment={removeAssignment}
						showPerDiem={false}
					/>
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button
						label="Close"
						onClick={closeModal}
						style="secondary"
					/>
				</CustomModal.Footer>
			</>
		);
	}, [
		assignableWorkOrders,
		assignedWorkOrders,
		closeModal,
		createAssignment,
		equipment,
		hasPermissionsToEditScheduleBoard,
		openEquipmentWorkOrderHistoryModal,
		removeAssignment,
		isAllowedToManagePerDiems,
		returnDate,
		loaned,
	]);

	const renderLoadingModalContent = React.useCallback(() => {
		return (
			<Loading onHide={closeModal} />
		);
	}, [closeModal]);

	return (
		<CustomModal
			className="resource-modal equipment"
			closeModal={closeModal}
			modalStyle="info"
			showModal={showEquipmentModal}
			size="xl"
		>
			{equipment ? renderDataModalContent() : renderLoadingModalContent()}
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const isAllowedToManagePerDiems: boolean = isAllowed(PagePermissions.COMPANY.PER_DIEMS, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		isAllowedToManagePerDiems,
		workOrdersByDateDictionary: state.scheduleBoard.workOrdersByDateDictionary,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EquipmentModal);
